import { Box, Button, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select, SvgIcon, TextField, Typography, useTheme } from '@mui/material';
import ScheduleTable from './TransactionsTable';
import { useTranslation } from 'react-i18next';
import { SearchSm } from '../../../assets';
import { Section } from '../../../components/ui/Section';
import React from 'react';
import { TransactionsContext, TransactionsContextValues } from '../contexts/TransactionsContext';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';
import dayjs from 'dayjs';
import { formatDateTimeForDisplay } from '../../../utils';

const TransactionsBox = () => {
  const { getDateOnly,
    handleSearchChange,
    handleStoreChange,
    handleTypeChange,
    handleReasonChange,
    handleSourceIDChange,
    handleSourceChange,
    handleStartDateChange,
    handleEndDateChange,
    handleClearButtonClick,
    handleSearchButtonClick,
    sources,
    isLoadingTypes,
    searchTerm,
    dropdownStore,
    stores,
    type,
    transactionTypes,
    reason,
    transactionReasons,
    sourceID,
    source,
    startDate,
    endDate,
    operationalStart,
    operationalEnd,
    isShiftEnabled,
  } = React.useContext(TransactionsContext) as TransactionsContextValues;

  const { t } = useTranslation('common');
  const theme = useTheme();

  return <>
    <Grid container
      sx={{ pt: '6px', pb: '12px' }}
      spacing={5}>
      <Grid item
        md={6}
        sm={6}
        xs={12}>
        <FormControl fullWidth
          size='small'>
          <TextField id="outlined-basic"
            label={t('transactions.searchSkuDescription')}
            size='small'
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange} />
        </FormControl>
      </Grid>
      <Grid item
        md={6}
        sm={6}
        xs={12}>
        <FormControl fullWidth
          size='small'>
          <InputLabel id="store-label">{t('transactions.store')}</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            label={t('transactions.store')}
            value={dropdownStore} // Set the value to the currently selected store
            onChange={handleStoreChange}
          >
            {stores.map((store) => {
              return (
                <MenuItem key={store.id}
                  value={store.id}>
                  {store.displayName}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        md={3}
        sm={4}
        xs={12}>
        <FormControl fullWidth
          size='small'>
          <InputLabel id="transaction-types-label">{t('transactions.transactionTypes')}</InputLabel>
          <Select
            labelId="transaction-types-label"
            id="transaction-types-select"
            value={type}
            label={t('transactions.transactionTypes')}
            onChange={handleTypeChange}
          >
            <MenuItem value="All">
              All
            </MenuItem>
            {!isLoadingTypes && transactionTypes.map((type) => {
              return <MenuItem key={type.type}
                value={type.type}>{type.type}</MenuItem>;
            })}
            {isLoadingTypes && (
              <MenuItem value="">
                <CircularProgress size={20}
                  sx={{ mr: 1 }} />
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        md={3}
        sm={4}
        xs={12}>
        <FormControl fullWidth
          size='small'>
          <InputLabel id="transaction-reasons-label">{t('transactions.transactionReasons')}</InputLabel>
          <Select
            labelId="transaction-reasons-label"
            id="transaction-reasons-select"
            value={reason}
            label={t('transactions.transactionReasons')}
            onChange={handleReasonChange}
          >
            <MenuItem value="All">
              <em>All</em>
            </MenuItem>
            {!isLoadingTypes && transactionReasons.map((reason) => {
              return <MenuItem key={reason.reason}
                value={reason.reason}>{reason.reason}</MenuItem>;
            })}
            {isLoadingTypes && (
              <MenuItem value="">
                <CircularProgress size={20}
                  sx={{ mr: 1 }} />
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        md={3}
        sm={4}
        xs={12}>
        <FormControl fullWidth
          size='small'>
          <TextField id="outlined-basic"
            label={t('transactions.sourceID')}
            size='small'
            variant="outlined"
            value={sourceID}
            onChange={handleSourceIDChange} />
        </FormControl>
      </Grid>
      <Grid item
        md={3}
        sm={4}
        xs={12}>
        <FormControl fullWidth
          size='small'>
          <InputLabel id="source-label">Source</InputLabel>
          <Select
            labelId="source-label"
            id="source-select"
            value={source}
            label="Source"
            onChange={handleSourceChange}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {sources.map((type) => {
              return <MenuItem key={type}
                value={type}>{type}</MenuItem>;
            })}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        md={3}
        sm={4}
        xs={12}>
        <FormControl fullWidth
          size='small'>
          <DatePickerLocalized label={t('transactions.startDate')}
            useCalendar={false}
            value={getDateOnly(startDate)}
            onChange={handleStartDateChange}
            maxDate={dayjs(endDate)} />
        </FormControl>
      </Grid>
      <Grid item
        md={3}
        sm={4}
        xs={12}>
        <FormControl fullWidth
          size='small'>
          <DatePickerLocalized label={t('transactions.endDate')}
            useCalendar={false}
            value={getDateOnly(endDate)}
            onChange={handleEndDateChange}
            minDate={dayjs(startDate)} />
        </FormControl>
      </Grid>
      <Grid item
        md={3}
        sm={6}
        xs={12}>
        <Button size='lg'
          variant='secondary'
          onClick={handleClearButtonClick}
          fullWidth>{t('clearAll')}</Button>
      </Grid>
      <Grid item
        md={3}
        sm={6}
        xs={12}>
        <Button size='lg'
          variant='primary'
          onClick={handleSearchButtonClick}
          startIcon={<SvgIcon><SearchSm></SearchSm></SvgIcon>}
          fullWidth>{t('search')}</Button>
      </Grid>
    </Grid>
    {operationalStart && operationalEnd && isShiftEnabled && (
      <Box
        sx={{
          mt: 2,
          display: 'inline-block',
          px: 2,
          py: 2,
          mb: 5,
          border: `1px solid ${theme.palette.secondary[300]}`,
          borderRadius: '8px',
        }}
      >
        <Typography
        >
          {`Showing transactions from ${formatDateTimeForDisplay(operationalStart)} to ${formatDateTimeForDisplay(operationalEnd)}`}
        </Typography>
      </Box>
    )}
    <Section>
      <ScheduleTable></ScheduleTable>
    </Section>
  </>;
};

export default TransactionsBox;
